// src/pages/OvertimeOverview.js

import React, { useState, useMemo, useEffect } from 'react';
import { Nav } from '../../components/nav';
import { useAuth } from '../../navigation/AuthProvider';
import "../css/Payroll.css";
import axios from 'axios';
import { BACKEND_URL } from '../../constants';
import { Toast } from '../../components/ToastPopup';
import { calculatePayForEmployee } from '../../utils/calculatePayForEmployee';

export const OvertimeOverview = () => {
  const {
    employees,
    timeSchemas,
    company,
    user,

    // needed for calculatePayForEmployee consistency
    pays,
    vacationPay,
    overtimeRules = [],
    obRules,
    absenceRules,
  } = useAuth();

  const [toastMessage, setToastMessage] = useState("");
  const [toastType, setToastType] = useState("success");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedSchema, setSelectedSchema] = useState("");
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [loading, setLoading] = useState(false);
  const [compactMode, setCompactMode] = useState(false);

  // 🔁 Lokal state för skift (ersätter draftSchedule)
  const [shifts, setShifts] = useState([]);

  const monthsInSwedish = [
    'Januari','Februari','Mars','April','Maj','Juni',
    'Juli','Augusti','September','Oktober','November','December'
  ];

  // --- helpers ---
  const parseMinutes = (str) => {
    const [h, m] = String(str || "0:0").split(":").map(Number);
    return (isFinite(h) ? h : 0) * 60 + (isFinite(m) ? m : 0);
  };

  const rangesOverlap = (start1, end1, start2, end2) =>
    Math.max(start1, start2) < Math.min(end1, end2);

  const normalizeForShift = (fromMin, toMin, sMin, eMin) => {
    let f = fromMin;
    let t = toMin;
    if (t <= f) t += 1440;
    if (eMin > 1440 && t <= sMin) {
      f += 1440;
      t += 1440;
    }
    return [f, t];
  };

  // 🔥 SAFE employeeId resolver (NEW)
  const getEmployeeId = (e) => {
    if (!e?.employeeId) return null;
    return typeof e.employeeId === "object"
      ? e.employeeId?._id
      : e.employeeId;
  };

  const isShiftCompleteForPayroll = (s) => {
    const empHasId = Array.isArray(s?.employees) &&
      s.employees.some(e => {
        const id = getEmployeeId(e);
        return !!id;
      });
    return !!(
      s &&
      s.status === "complete" &&
      s.date &&
      typeof s.startTime === "string" && s.startTime.includes(":") &&
      typeof s.endTime === "string" && s.endTime.includes(":") &&
      empHasId
    );
  };

  useEffect(() => {
    const fetchShifts = async () => {
      setLoading(true);
      try {
        const { data } = await axios.get(
          `${BACKEND_URL}/get-shifts-for-month`,
          {
            params: {
              userId: user._id,
              companyId: company._id,
              selectedMonth,
              selectedYear
            }
          }
        );
        setShifts(Array.isArray(data.shifts) ? data.shifts : []);
      } catch (err) {
        console.error("Nätverksfel eller serverfel:", err);
        const serverMsg = err.response?.data?.message;
        setToastType("error");
        setToastMessage(serverMsg || "Kunde inte hämta skift – se konsolen");
      } finally {
        setLoading(false);
      }
    };

    fetchShifts();
  }, [company._id, selectedMonth, selectedYear, user._id, setShifts]);

  const filteredEmployees = useMemo(() =>
    employees.filter(emp => {
      const text = searchQuery.toLowerCase();
      return (
        (emp.name || "").toLowerCase().includes(text) ||
        (emp.email || "").toLowerCase().includes(text) ||
        (emp.role || "").toLowerCase().includes(text)
      );
    })
  , [employees, searchQuery]);

  const overtimeColumns = useMemo(() => {
    if (Array.isArray(overtimeRules) && overtimeRules.length > 0) {
      return overtimeRules.map(r => ({
        id: String(r._id),
        name: r.name || String(r._id),
      }));
    }

    const set = new Set();
    shifts.forEach(s => {
      if (!isShiftCompleteForPayroll(s)) return;
      (s.employees || []).forEach(e => {
        (e.overtime || []).forEach(ot => {
          const id = typeof ot?.type === 'object' ? ot.type?._id : ot?.type;
          if (id) set.add(String(id));
        });
      });
    });

    return Array.from(set).map(id => ({ id, name: id }));
  }, [overtimeRules, shifts]);

  const relevantShiftsForEmp = (emp) => {
    return shifts.filter(s => {
      if (!isShiftCompleteForPayroll(s)) return false;

      const d = new Date(s.date);
      if (isNaN(d)) return false;

      const sameMonth = d.getMonth() + 1 === selectedMonth;
      const sameYear  = d.getFullYear() === selectedYear;
      const sameSchema = !selectedSchema || String(s.timeSchema) === String(selectedSchema);

      const hasEmp = Array.isArray(s.employees) && s.employees.some(e => {
        const empId = getEmployeeId(e); // 🔥 FIX
        if (!empId) return false;
        return String(empId) === String(emp._id);
      });

      return sameMonth && sameYear && sameSchema && hasEmp;
    });
  };

  const computePerRuleOvertimeHours = (shift, assignment) => {
    const out = {};
    if (!shift?.startTime || !shift?.endTime) return out;

    const sMin = parseMinutes(shift.startTime);
    let eMin = parseMinutes(shift.endTime);
    if (eMin <= sMin) eMin += 1440;

    const absences = Array.isArray(assignment?.absences) ? assignment.absences : [];
    const breaks = Array.isArray(assignment?.breaks) ? assignment.breaks : [];
    const overtimeArr = Array.isArray(assignment?.overtime) ? assignment.overtime : [];

    overtimeArr.forEach(ot => {
      const ruleId = String(typeof ot?.type === 'object' ? ot.type?._id : ot?.type || "");
      if (!ruleId || !ot?.from || !ot?.to) return;

      let fromMin = parseMinutes(ot.from);
      let toMin = parseMinutes(ot.to);
      [fromMin, toMin] = normalizeForShift(fromMin, toMin, sMin, eMin);

      for (let m = fromMin; m < toMin; m++) {
        const inBreak = breaks.some(b => {
          if (!b.from || !b.to) return false;
          let bFrom = parseMinutes(b.from);
          let bTo = parseMinutes(b.to);
          if (bTo <= bFrom) bTo += 1440;
          if (eMin > 1440 && bTo <= sMin) { bFrom += 1440; bTo += 1440; }
          return m >= bFrom && m < bTo;
        });
        if (inBreak) continue;

        const inAbsence = absences.some(abs => {
          if (abs.appliesWholeShift) return true;
          if (!abs.from || !abs.to) return false;
          let aFrom = parseMinutes(abs.from);
          let aTo = parseMinutes(abs.to);
          [aFrom, aTo] = normalizeForShift(aFrom, aTo, sMin, eMin);
          return m >= aFrom && m < aTo;
        });
        if (inAbsence) continue;

        out[ruleId] = (out[ruleId] || 0) + 1;
      }
    });

    Object.keys(out).forEach(k => {
      out[k] = out[k] / 60;
    });

    return out;
  };

  const tableData = useMemo(() =>
    filteredEmployees.map(emp => {
      const empShifts = relevantShiftsForEmp(emp);

      const { summary } = calculatePayForEmployee({
        employee: emp,
        shifts: empShifts,
        payRates: pays || [],
        vacationPay: vacationPay || {},
        overtimeRules: overtimeRules || [],
        obRules: obRules || [],
        absenceRules: absenceRules || [],
        returnDetailed: true,
      });

      const perTypeMap = {};
      empShifts.forEach(s => {
        const assignment = (s.employees || []).find(x => {
          const id = getEmployeeId(x); // 🔥 FIX
          if (!id) return false;
          return String(id) === String(emp._id);
        });

        if (!assignment) return;

        const perRule = computePerRuleOvertimeHours(s, assignment);
        Object.entries(perRule).forEach(([ruleId, hrs]) => {
          perTypeMap[ruleId] = (perTypeMap[ruleId] || 0) + Number(hrs || 0);
        });
      });

      const perTypeArray = overtimeColumns.map(col => Number(perTypeMap[col.id] || 0));

      return {
        emp,
        totalOvertime: Number(summary?.overtimeHours || 0),
        perTypeArray
      };
    })
  , [
    filteredEmployees,
    shifts,
    selectedMonth,
    selectedYear,
    selectedSchema,
    overtimeColumns,
    pays, vacationPay, overtimeRules, obRules, absenceRules
  ]);

  return (
    <div className="payroll-page">
      <Nav />
      <div className="payroll-container">
        <h1 style={{ fontSize: "1.8rem", fontWeight: 700, marginBottom: 16 }}>
          Övertid-översikt
        </h1>

        <div className="top-inputs-container" style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
          <div className="search-bar-container" style={{ flex: 1 }}>
            <input
              type="text"
              placeholder="Sök efter namn, email eller roll..."
              className="search-input"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
            />
          </div>

          <select className="schema-select payroll-select" value={selectedSchema} onChange={e => setSelectedSchema(e.target.value)}>
            <option value="">-- Visa alla scheman --</option>
            {timeSchemas.map(s => (
              <option key={s._id} value={s._id}>{s.name}</option>
            ))}
          </select>

          <select className="month-select payroll-select" value={selectedMonth} onChange={e => setSelectedMonth(+e.target.value)}>
            {monthsInSwedish.map((m, i) => (
              <option key={i} value={i + 1}>{m}</option>
            ))}
          </select>

          <select className="year-select payroll-select" value={selectedYear} onChange={e => setSelectedYear(+e.target.value)}>
            {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i).map(y => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>

          <label style={{ display: "inline-flex", alignItems: "center", gap: 6, marginLeft: 8, fontSize: 13, cursor: "pointer" }}>
            <input type="checkbox" checked={compactMode} onChange={e => setCompactMode(e.target.checked)} />
            Kompakt läge (endast total)
          </label>
        </div>

        {loading ? (
          <div className="spinner-container"><div className="spinner" /></div>
        ) : (
          <div className="payroll-grid" style={{ overflowX: 'auto' }}>
            <div className="payroll-header" style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              minWidth: compactMode
                ? 520
                : 320 + Math.max(0, overtimeColumns.length) * 120
            }}>
              <span style={{ flex: 2 }}>Namn</span>
              <span style={{ flex: 1, textAlign: 'center' }}>Total övertid</span>
              {!compactMode && overtimeColumns.map(col => (
                <span key={col.id} style={{ flex: 1, textAlign: 'center' }}>
                  {col.name}
                </span>
              ))}
            </div>

            {tableData.map(({ emp, totalOvertime, perTypeArray }) => (
              <div key={emp._id} className="payroll-row" style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                minWidth: compactMode
                  ? 520
                  : 320 + Math.max(0, overtimeColumns.length) * 120
              }}>
                <span style={{ flex: 2 }}>{emp.name}</span>
                <span style={{ flex: 1, textAlign: 'center' }}>
                  {Number(totalOvertime || 0).toFixed(2)}
                </span>
                {!compactMode && perTypeArray.map((h, i) => (
                  <span key={`${emp._id}-${i}`} style={{ flex: 1, textAlign: 'center' }}>
                    {Number(h || 0).toFixed(2)}
                  </span>
                ))}
              </div>
            ))}
          </div>
        )}

        <Toast
          message={toastMessage}
          type={toastType}
          onClose={() => setToastMessage("")}
        />
      </div>
    </div>
  );
};

export default OvertimeOverview;